import { useState } from 'react';
import Button from '../ui/Button';
import { Select, Input } from '../ui/Input';

// Governed bulk-edit bar. Shows when ≥1 grid row is selected and lets the user
// set one field to one value across the whole selection. The page wires onApply
// to its existing dry-run + audit import endpoint — this component never writes.
//
//   fields        [{ key, label }]            the editable columns to choose from
//   valueOptions  [{ value, label }] | null   if given, the value is a Select
//                                             (e.g. ranging: Ranged / De-ranged);
//                                             otherwise a free-text Input
//   onApply(field, value)                      kicks off the page's dry-run flow
//   children                                   optional page-specific actions on
//                                              the selection, drawn as a second
//                                              row so they appear where the user
//                                              is already looking, not below the grid
export default function BulkEditBar({ selectedCount, fields, valueOptions, onApply, onClear, applying, noun = 'rows', children }) {
  const [picked, setPicked] = useState(fields[0]?.key ?? '');
  const [value, setValue] = useState(valueOptions ? valueOptions[0]?.value ?? '' : '');

  // Derive a valid field during render (no setState-in-effect): if the picked
  // field is no longer available — e.g. the ranging matrix swapped its SKU
  // columns between data loads — fall back to the first field.
  const field = fields.some((f) => f.key === picked) ? picked : (fields[0]?.key ?? '');

  if (!selectedCount) return null;

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
      background: 'var(--accent-soft)', border: '1px solid var(--accent)',
      borderRadius: 'var(--radius)', padding: '8px 12px',
    }}>
      <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>
        {selectedCount.toLocaleString()} {noun} selected
      </span>
      <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Set</span>
      <div style={{ width: 200 }}>
        <Select value={field} onChange={(e) => setPicked(e.target.value)}>
          {fields.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
        </Select>
      </div>
      <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>=</span>
      <div style={{ width: 200 }}>
        {valueOptions
          ? (
            <Select value={value} onChange={(e) => setValue(e.target.value)}>
              {valueOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </Select>
          )
          : <Input value={value} onChange={(e) => setValue(e.target.value)} placeholder="New value (blank = clear)" />}
      </div>
      <Button size="sm" variant="primary" disabled={applying || !field} onClick={() => onApply(field, value)}>
        {applying ? 'Preparing…' : 'Apply to selected'}
      </Button>
      <Button size="sm" variant="ghost" onClick={onClear}>Clear</Button>
      {children && (
        <div style={{ flexBasis: '100%', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {children}
        </div>
      )}
    </div>
  );
}
